import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import '../styles/ImprimirBoleto.css'
import { bff, BffError } from '../lib/bff'
import { LampContainer } from '@/components/ui/lamp'

// ── Tipos ────────────────────────────────────────────────────────────────────

interface BoletoItem {
  vencimento: string // DD.MM.YYYY
  urlBoleto:  string
}

interface RespostaBoletos {
  cnpj:       string
  codCliente: number
  boletos:    BoletoItem[]
}

// ── Helpers ──────────────────────────────────────────────────────────────────

type Status = 'vencido' | 'hoje' | 'breve' | 'normal'

function parseData(ddmmyyyy: string): Date {
  const [d, m, y] = ddmmyyyy.split('.')
  return new Date(Number(y), Number(m) - 1, Number(d))
}

function statusBoleto(vencimento: string): Status {
  const hoje = new Date()
  hoje.setHours(0, 0, 0, 0)
  const venc = parseData(vencimento)
  const diff = Math.ceil((venc.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24))
  if (diff < 0)   return 'vencido'
  if (diff === 0) return 'hoje'
  if (diff <= 7)  return 'breve'
  return 'normal'
}

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ']

// "DD.MM.YYYY" → partes para o calendarinho do boleto
function partesData(ddmmyyyy: string) {
  const [d, m, y] = ddmmyyyy.split('.')
  return { dia: d, mes: MESES[Number(m) - 1] ?? m, ano: y }
}

const statusLabel: Record<Status, string> = {
  vencido: 'Vencido',
  hoje:    'Vence hoje',
  breve:   'Vence em breve',
  normal:  'A vencer',
}

const statusIcon: Record<Status, string> = {
  vencido: 'fa-circle-exclamation',
  hoje:    'fa-circle-exclamation',
  breve:   'fa-clock',
  normal:  'fa-circle-check',
}

function formatarCNPJ(valor: string) {
  const d = valor.replace(/\D/g, '').slice(0, 14)
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2')
}

function formatarCNPJDisplay(cnpj: string) {
  return formatarCNPJ(cnpj)
}

// ── Estado ───────────────────────────────────────────────────────────────────

type Estado =
  | { tipo: 'idle' }
  | { tipo: 'carregando'; dados?: RespostaBoletos } // mantém o resultado anterior visível durante nova consulta
  | { tipo: 'resultado'; dados: RespostaBoletos }
  | { tipo: 'erro'; mensagem: string }

// ── Componente ───────────────────────────────────────────────────────────────

export default function ImprimirBoleto() {
  const [cnpj,   setCnpj]   = useState('')
  const [codigo, setCodigo] = useState('')
  const [estado, setEstado] = useState<Estado>({ tipo: 'idle' })

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const cnpjLimpo   = cnpj.replace(/\D/g, '')
    const codigoLimpo = codigo.trim()

    if (cnpjLimpo.length !== 14) {
      setEstado({ tipo: 'erro', mensagem: 'Informe um CNPJ válido (14 dígitos).' })
      return
    }
    if (!codigoLimpo || isNaN(Number(codigoLimpo)) || Number(codigoLimpo) <= 0) {
      setEstado({ tipo: 'erro', mensagem: 'Informe um código de cliente válido.' })
      return
    }

    setEstado(atual => ({
      tipo: 'carregando',
      dados: atual.tipo === 'resultado' ? atual.dados : undefined,
    }))

    try {
      const resposta = await bff.post<RespostaBoletos>('/v1/boletos', {
        cnpj:       cnpjLimpo,
        codcliente: Number(codigoLimpo),
      })

      if (resposta.boletos.length === 0) {
        setEstado({ tipo: 'erro', mensagem: 'Nenhum boleto encontrado para este CNPJ e código.' })
        return
      }

      setEstado({ tipo: 'resultado', dados: resposta })
    } catch (err) {
      if (err instanceof BffError) {
        if (err.status === 404 || err.status === 400) {
          setEstado({ tipo: 'erro', mensagem: 'Nenhum boleto encontrado para este CNPJ e código.' })
        } else if (err.status === 503) {
          setEstado({ tipo: 'erro', mensagem: 'Serviço temporariamente indisponível. Tente novamente em instantes.' })
        } else {
          setEstado({ tipo: 'erro', mensagem: err.message })
        }
      } else {
        setEstado({ tipo: 'erro', mensagem: 'Não foi possível conectar ao servidor. Verifique sua conexão.' })
      }
    }
  }

  function handleNovaPesquisa() {
    setEstado({ tipo: 'idle' })
    setCnpj('')
    setCodigo('')
  }

  const dados = estado.tipo === 'resultado' || estado.tipo === 'carregando'
    ? estado.dados
    : undefined

  const vencidos = dados
    ? dados.boletos.filter(b => statusBoleto(b.vencimento) === 'vencido').length
    : 0

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* ══ HERO ══ */}
      <LampContainer
        className="min-h-[22rem] md:min-h-[25rem] rounded-none pt-36 md:pt-44"
        lampClassName="translate-y-10"
        contentClassName="-translate-y-32"
        overlay={
          <div
            className="absolute inset-0 z-10 pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(rgba(26, 62, 115, 0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(26, 62, 115, 0.15) 1px, transparent 1px)',
              backgroundSize: '48px 48px',
            }}
          />
        }
      >
        <motion.h1
          initial={{ opacity: 0.5, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{
            delay: 0.3,
            duration: 0.8,
            ease: 'easeInOut',
          }}
          className="mt-8 bg-gradient-to-br from-slate-300 to-slate-500 py-4 bg-clip-text text-center text-3xl font-medium tracking-tight text-transparent sm:text-4xl md:text-7xl px-4"
        >
          Imprimir Boleto
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{
            delay: 0.45,
            duration: 0.8,
            ease: 'easeInOut',
          }}
          className="mt-2 max-w-xl text-center text-base text-slate-400 px-4"
        >
          Informe seu CNPJ e código de cliente para consultar e baixar seus boletos em PDF.
        </motion.p>
      </LampContainer>

      {/* ══ CORPO ══ */}
      <div className="ib-body">
        <div className="ib-body-inner">

          {/* ── Coluna esquerda (centralizada até haver resultado) ── */}
          <motion.div
            layout="position"
            // Na volta ao centro, espera o resultado sumir antes de deslizar
            transition={{ duration: 0.5, ease: 'easeInOut', delay: dados ? 0 : 0.2 }}
            className="ib-col-left"
          >

            {/* Formulário */}
            <div className="ib-card">
              <div className="ib-card-head">
                <div className="ib-card-icon">
                  <i className="fa-solid fa-file-invoice-dollar" aria-hidden="true" />
                </div>
                <div>
                  <h2 className="ib-card-title">Pesquisar boletos</h2>
                  <p className="ib-card-sub">
                    Dúvidas? Ligue <strong>(63)&nbsp;98404-2819</strong>
                  </p>
                </div>
              </div>

              <form className="ib-form" onSubmit={handleSubmit}>
                <div className="ib-field">
                  <label htmlFor="cnpj" className="ib-label">CNPJ</label>
                  <div className="ib-input-wrap">
                    <i className="fa-regular fa-building" aria-hidden="true" />
                    <input
                      id="cnpj"
                      name="cnpj"
                      className="ib-input"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="00.000.000/0000-00"
                      maxLength={18}
                      value={cnpj}
                      onChange={e => setCnpj(formatarCNPJ(e.target.value))}
                    />
                  </div>
                </div>
                <div className="ib-field">
                  <label htmlFor="codigo" className="ib-label">Código do cliente</label>
                  <div className="ib-input-wrap">
                    <i className="fa-solid fa-hashtag" aria-hidden="true" />
                    <input
                      id="codigo"
                      name="codigo"
                      className="ib-input"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      pattern="[0-9]*"
                      placeholder="Ex: 4052"
                      value={codigo}
                      onChange={e => setCodigo(e.target.value.replace(/\D/g, ''))}
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="ib-btn-submit"
                  disabled={estado.tipo === 'carregando'}
                >
                  {estado.tipo === 'carregando' ? (
                    <><i className="fa-solid fa-circle-notch fa-spin" aria-hidden="true" />Consultando…</>
                  ) : (
                    <><i className="fa-solid fa-magnifying-glass" aria-hidden="true" />Pesquisar Boletos</>
                  )}
                </button>
              </form>

              {/* Erro */}
              {estado.tipo === 'erro' && (
                <div className="ib-alert-error" role="alert">
                  <i className="fa-solid fa-circle-xmark" aria-hidden="true" />
                  <span>{estado.mensagem}</span>
                </div>
              )}

              {/* Ajuda */}
              <a href="https://wa.me/5563984042819" target="_blank" rel="noreferrer" className="ib-ajuda">
                <i className="fa-brands fa-whatsapp" aria-hidden="true" />
                <span>
                  Não encontrou seus boletos? Fale pelo WhatsApp <strong>(63)&nbsp;98404-2819</strong>
                </span>
              </a>
            </div>
          </motion.div>

          {/* ── Coluna direita (resultado) ── */}
          {/* popLayout tira o resultado do fluxo ao sair, para o formulário voltar animado */}
          <AnimatePresence mode="popLayout">
            {dados && (
              <motion.div
                key="resultado"
                className="ib-col-right"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0, transition: { duration: 0.4, delay: 0.25, ease: 'easeOut' } }}
                exit={{ opacity: 0, x: 40, transition: { duration: 0.25, ease: 'easeIn' } }}
              >
                <div className="ib-card ib-card--resultado">
                  <div className="ib-result-header">
                    <div>
                      <div className="ib-result-label">CNPJ</div>
                      <div className="ib-result-cnpj">{formatarCNPJDisplay(dados.cnpj)}</div>
                      <div className="ib-result-cliente">Código do cliente: {dados.codCliente}</div>
                    </div>
                    <div className="ib-result-contagem">
                      <span className="ib-result-num">{dados.boletos.length}</span>
                      <span className="ib-result-label">{dados.boletos.length === 1 ? 'boleto' : 'boletos'}</span>
                      {vencidos > 0 && (
                        <span className="ib-result-vencidos">
                          {vencidos} vencido{vencidos > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  <ul className="ib-boleto-list">
                    {dados.boletos.map((boleto) => {
                      const st = statusBoleto(boleto.vencimento)
                      const { dia, mes, ano } = partesData(boleto.vencimento)
                      return (
                        <li key={boleto.urlBoleto} className={`ib-boleto-row st-${st}`}>
                          <div className="ib-boleto-data" aria-hidden="true">
                            <span className="ib-boleto-dia">{dia}</span>
                            <span className="ib-boleto-mes">{mes} {ano}</span>
                          </div>
                          <div className="ib-boleto-info">
                            <span className="ib-boleto-venc">Vencimento {boleto.vencimento.replace(/\./g, '/')}</span>
                            <span className={`ib-badge ib-badge-${st}`}>
                              <i className={`fa-solid ${statusIcon[st]}`} aria-hidden="true" />
                              {statusLabel[st]}
                            </span>
                          </div>
                          <a
                            href={boleto.urlBoleto}
                            target="_blank"
                            rel="noreferrer"
                            className="ib-download-link"
                          >
                            <i className="fa-solid fa-file-pdf" aria-hidden="true" />
                            Baixar PDF
                          </a>
                        </li>
                      )
                    })}
                  </ul>

                  <div className="ib-result-footer">
                    <button type="button" className="ib-btn-nova-pesquisa" onClick={handleNovaPesquisa}>
                      <i className="fa-solid fa-arrow-left" aria-hidden="true" />
                      Nova pesquisa
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

        </div>
      </div>

    </div>
  )
}
